import React from 'react';
import { ArrowRight, Check, MessageSquareText, ShieldCheck } from 'lucide-react';
const beforeSignals = ['“You are too sensitive. That never happened.”', 'A confusing thread with no clear pattern to point to.', 'Second-guessing what the conversation actually meant.'];
const afterSignals = ['Potential minimization and reality-denial language flagged.', 'Patterns organized into clear, reviewable signals.', 'Specific moments to revisit with more context and care.'];
export function ComparisonSection() {
  return <section id="comparison" className="border-y border-gray-900 bg-[#050505] py-20 md:py-28" data-id="element-2157">
      <div className="mx-auto max-w-6xl px-6" data-id="element-2158">
        <header className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-2159">
          <div className="mb-5 inline-flex items-center gap-2 border border-[#FF0000]/30 bg-[#FF0000]/10 px-3 py-1.5" data-id="element-2160">
            <ShieldCheck size={14} className="text-[#FF0000]" data-id="element-2161" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-2162">
              Clarity, not guesswork
            </span>
          </div>
          <h2 className="text-4xl font-black leading-tight tracking-tight text-white md:text-5xl" data-id="element-2163">
            See the difference between a difficult conversation and a named
            pattern.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-gray-400 md:text-lg" data-id="element-2164">
            ToxicDetector helps turn scattered messages into a structured view
            of the communication signals worth paying attention to.
          </p>
        </header>

        <div className="grid gap-5 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch" data-id="element-2165">
          <article className="border border-gray-800 bg-[#0A0A0A] p-6 md:p-8" data-id="element-2166">
            <div className="mb-7 flex items-center gap-3" data-id="element-2167">
              <span className="flex h-10 w-10 items-center justify-center border border-gray-700 bg-black text-sm font-black text-gray-400" data-id="element-2168">
                01
              </span>
              <div data-id="element-2169">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500" data-id="element-2170">
                  Before a scan
                </p>
                <h3 className="mt-1 text-xl font-bold text-white" data-id="element-2171">
                  The conversation feels unclear
                </h3>
              </div>
            </div>
            <ul className="space-y-3" data-id="element-2172">
              {beforeSignals.map(signal => <li key={signal} className="border border-gray-800 bg-black/40 p-4 text-sm leading-relaxed text-gray-400" data-id="element-2173">
                  {signal}
                </li>)}
            </ul>
          </article>

          <div className="flex items-center justify-center py-1 lg:py-0" aria-hidden="true" data-id="element-2174">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF0000]/40 bg-[#050505] text-[#FF0000]" data-id="element-2175">
              <ArrowRight size={20} className="rotate-90 lg:rotate-0" data-id="element-2176" />
            </div>
          </div>

          <article className="border border-[#FF0000]/40 bg-[#0A0A0A] p-6 md:p-8" data-id="element-2177">
            <div className="mb-7 flex items-center gap-3" data-id="element-2178">
              <span className="flex h-10 w-10 items-center justify-center border border-[#FF0000]/40 bg-[#FF0000]/10 text-sm font-black text-[#FF0000]" data-id="element-2179">
                02
              </span>
              <div data-id="element-2180">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF0000]" data-id="element-2181">
                  After a scan
                </p>
                <h3 className="mt-1 text-xl font-bold text-white" data-id="element-2182">
                  The signals have language
                </h3>
              </div>
            </div>
            <ul className="space-y-3" data-id="element-2183">
              {afterSignals.map(signal => <li key={signal} className="flex gap-3 border border-red-500/20 bg-red-500/5 p-4 text-sm leading-relaxed text-gray-200" data-id="element-2184">
                  <Check size={17} className="mt-0.5 shrink-0 text-[#FF0000]" strokeWidth={3} data-id="element-2185" />
                  <span data-id="element-2186">{signal}</span>
                </li>)}
            </ul>
          </article>
        </div>

        <div className="mt-10 border border-gray-800 bg-black p-6 md:flex md:items-center md:justify-between md:gap-8 md:p-8" data-id="element-2187">
          <div className="flex gap-4" data-id="element-2188">
            <MessageSquareText className="mt-1 shrink-0 text-[#FFD700]" size={22} data-id="element-2189" />
            <div data-id="element-2190">
              <h3 className="text-lg font-bold text-white" data-id="element-2191">
                A clearer record for your next decision
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-400" data-id="element-2192">
                Review the language, context, and repeated signals in one place.
                Analysis supports reflection; it does not diagnose intent.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center" data-id="element-2193">
          <h3 className="text-2xl font-bold text-white" data-id="element-2194">
            Ready for emotional clarity?
          </h3>
          <p className="mt-3 text-sm font-mono text-gray-400" data-id="element-2195">
            Stop guessing the dynamic. Start naming it.
          </p>
          <a href="/signup" className="mt-7 inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF0000] px-8 py-4 font-black uppercase tracking-wider text-white transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2196">
            Start Free Scan
            <ArrowRight size={18} data-id="element-2197" />
          </a>
        </div>
      </div>
    </section>;
}